/**
 * MicCountdownOverlay — extracted from ConversationsChat.js (Session 5 refactor).
 * Jul 2026
 *
 * Bundles the two related pre-microphone-open visual moments:
 *   1. `micCountdown` overlay (Sprint 207P): 2-second full-screen countdown
 *      that fires between the user tap and the actual `getUserMedia()`
 *      call, so teens don't start speaking before the mic is live.
 *   2. `micJustOpened` cue (Sprint 207Z): 1.4-second toast confirming the
 *      mic actually opened so it's safe to speak.
 *
 * Bug fixed during extraction: the pre-refactor `ConversationsChat.js`
 * rendered BOTH blocks TWICE (once at lines 599-645, again at 738-784).
 * Since both branches gated on the same `micCountdown !== null` flag they
 * always rendered together — invisible visually because they overlapped
 * exactly, but wasteful (2× DOM cost) and would have shown a duplicate
 * if either flag briefly diverged. Extraction collapses to a single render.
 */
import React from 'react';

export const MicCountdownOverlay = ({ micCountdown, micJustOpened, stopListening }) => (
  <>
    {micCountdown !== null && (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60]"
        data-testid="mic-countdown-overlay"
        role="status"
        aria-live="polite"
      >
        <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center">
          <div
            className="mx-auto mb-6 w-28 h-28 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-6xl font-black animate-pulse shadow-xl"
            data-testid="mic-countdown-digit"
          >
            {micCountdown}
          </div>
          <h3 className="text-xl font-bold text-gray-900">
            Opening mic in {micCountdown}…
          </h3>
          <p className="text-sm text-gray-700 mt-2 font-semibold">
            Don&apos;t speak yet — wait for the green &quot;Listening&quot; indicator.
          </p>
          <button
            onClick={stopListening}
            className="mt-6 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm font-medium"
            data-testid="mic-countdown-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    )}

    {micJustOpened && (
      <div
        className="fixed top-1/4 left-1/2 -translate-x-1/2 z-[60] pointer-events-none"
        data-testid="mic-listening-cue"
        role="status"
        aria-live="polite"
      >
        <div className="px-6 py-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-base shadow-2xl flex items-center gap-2 animate-pulse">
          <span className="w-2.5 h-2.5 rounded-full bg-white animate-ping" />
          Listening — speak now
        </div>
      </div>
    )}
  </>
);

export default MicCountdownOverlay;
